<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { salesReviewNavigationState } from '../composables/salesReviewNavigationState';
import { sectionLabels } from '../composables/salesPartyFormOptions';
import SalesReviewCorrectionCountBadge from './SalesReviewCorrectionCountBadge.vue';

const props = defineProps({
  activeSection: { type: String, required: true },
  sections: { type: Array, default: () => [] },
  isComplete: { type: Function, required: true },
  reviewMode: { type: Boolean, default: false },
  reviewCount: { type: Number, default: 0 },
  canEdit: { type: Boolean, default: false },
  partyKind: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  sectionChecks: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['select', 'save']);
const { t } = useI18n();
const labels = computed(() => sectionLabels(t));
const labelFor = section =>
  props.partyKind === 'pessoa_juridica' && section === 'address'
    ? t('SALES_PARTIES.SECTIONS.COMPANY_ADDRESS')
    : labels.value[section];
const isSelectable = section => {
  if (props.reviewMode) return true;

  return props.activeSection === section || props.isComplete(section);
};
const navigationState = section =>
  salesReviewNavigationState({
    active: props.activeSection === section,
    checks: props.sectionChecks[section] || [],
    isComplete: props.isComplete(section),
    reviewMode: props.reviewMode,
  });
const navigationIcon = section => {
  if (!props.reviewMode && props.activeSection === section) return 'pencil';

  return navigationState(section).icon;
};
const statusClass = section => {
  const classes = {
    active: 'bg-[#E8F7F6] text-[#009F94]',
    default: 'text-[#A7B7BE] hover:bg-[#F5F8F9]',
    pending: 'bg-red-50 text-red-600 hover:bg-red-100',
    saved: 'text-[#464F53] hover:bg-[#F5F8F9]',
  };

  return classes[navigationState(section).state];
};
const statusIconClass = section => {
  const classes = {
    active: 'text-[#009F94]',
    default: 'text-[#A7B7BE]',
    pending: 'text-red-600',
    saved: 'text-[#009F94]',
  };

  return classes[navigationState(section).state];
};
</script>

<template>
  <aside
    data-test="sales-party-registration-nav"
    class="hidden shrink-0 flex-col border-r border-[#DCE3E6] bg-white font-roboto min-[701px]:flex"
    :class="
      reviewMode
        ? 'h-[calc(100vh-66px)] w-[295px]'
        : 'h-[calc(100vh-66px)] w-[287px]'
    "
  >
    <nav
      class="min-h-0 flex-1 overflow-y-auto"
      :aria-label="t('SALES_PARTIES.ARIA.SECTIONS')"
    >
      <SalesReviewCorrectionCountBadge v-if="reviewMode" :count="reviewCount" />
      <ol class="grid list-none gap-0">
        <li v-for="section in sections" :key="section">
          <button
            type="button"
            class="flex h-[48px] w-full items-center gap-4 px-5 text-left text-base font-bold transition disabled:cursor-not-allowed disabled:opacity-60"
            :class="statusClass(section)"
            :disabled="!isSelectable(section)"
            @click="emit('select', section)"
          >
            <span
              class="size-5 shrink-0"
              :class="[
                navigationIcon(section) === 'check'
                  ? 'i-lucide-check'
                  : 'i-lucide-pencil',
                statusIconClass(section),
              ]"
              :data-test="`review-state-${section}`"
              aria-hidden="true"
            />
            <span>{{ labelFor(section) }}</span>
          </button>
        </li>
      </ol>
    </nav>

    <div class="border-t border-[#DCE3E6] p-[22px] min-[701px]:pb-[29px]">
      <button
        data-test="sales-party-registration-nav-save"
        type="button"
        class="flex h-[50px] w-[150px] items-center justify-center gap-2 rounded-[10px] border border-[#009F94] bg-white text-base font-bold text-[#009F94] transition hover:bg-[#E8F7F6] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!canEdit || saving"
        @click="emit('save')"
      >
        <span class="i-lucide-log-out size-5" aria-hidden="true" />
        {{ t('SALES_PARTIES.ACTIONS.SAVE_EXIT') }}
      </button>
    </div>
  </aside>
</template>
